/**
 * useAnalysis — fetches and polls the analysis for a single issue.
 *
 * State machine:
 *   idle           — initial state before first GET response
 *   not_started    — GET returned 404 (no analysis ever requested)
 *   queued         — POST returned 202 / GET returned {status:'queued'}
 *   running        — GET returned {status:'running'}
 *   completed      — GET returned AnalysisResult
 *   failed         — 3 consecutive poll errors (or unexpected error on request)
 *
 * Polling: every 2 s while queued or running. Stops on terminal states or unmount.
 * Consecutive-error guard: transitions to 'failed' after MAX_CONSECUTIVE_ERRORS
 * consecutive poll failures to avoid infinite loops on persistent backend error.
 *
 * Sprint 2.5: queued/running states carry current_round + current_tool from the
 * agentic backend, displayed in the loading skeleton.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { analyses } from '@/helpers/ApiHelper';
import { ApiError } from '@/helpers/ApiHelper';
import type { AnalysisResult, AnalysisModel, PromptVersion } from '@/types/analysis';
import { isAnalysisFailed, isAnalysisResult } from '@/types/analysis';

const POLL_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_ERRORS = 3;

export type AnalysisState =
  | { status: 'idle' }
  | { status: 'not_started' }
  | { status: 'queued'; current_round: number | null; current_tool: string | null }
  | { status: 'running'; current_round: number | null; current_tool: string | null }
  | { status: 'completed'; result: AnalysisResult }
  | { status: 'failed'; message: string };

export interface UseAnalysisReturn {
  state: AnalysisState;
  request: (model: AnalysisModel, promptVersion?: PromptVersion) => Promise<void>;
  refetch: () => void;
}

export function useAnalysis(issueId: string): UseAnalysisReturn {
  const [state, setState] = useState<AnalysisState>({ status: 'idle' });

  // Use refs for interval id and consecutive error counter so we never
  // depend on stale state inside interval closures.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const consecutiveErrorsRef = useRef(0);
  // Track whether we're currently polling so we don't start two loops.
  const isPollingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
    consecutiveErrorsRef.current = 0;
  }, []);

  const applyGetResponse = useCallback(
    (data: Awaited<ReturnType<typeof analyses.get>>) => {
      consecutiveErrorsRef.current = 0;
      if (isAnalysisFailed(data)) {
        // Terminal failure — stop polling and surface the persisted error_message
        // through the existing 'failed' render branch in IssueDetailPage.
        stopPolling();
        setState({ status: 'failed', message: data.error_message });
        return;
      }
      if (isAnalysisResult(data)) {
        stopPolling();
        setState({ status: 'completed', result: data });
      } else {
        // still in flight — carry round/tool info for loading skeleton
        setState({
          status: data.status,
          current_round: data.current_round,
          current_tool: data.current_tool,
        });
      }
    },
    [stopPolling],
  );

  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    consecutiveErrorsRef.current = 0;

    intervalRef.current = setInterval(() => {
      analyses
        .get(issueId)
        .then((data) => {
          applyGetResponse(data);
        })
        .catch((err: unknown) => {
          consecutiveErrorsRef.current += 1;
          const msg =
            err instanceof Error ? err.message : String(err);
          console.error(`[useAnalysis] poll error #${consecutiveErrorsRef.current}:`, msg);
          if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
            stopPolling();
            setState({ status: 'failed', message: msg });
          }
        });
    }, POLL_INTERVAL_MS);
  }, [issueId, applyGetResponse, stopPolling]);

  // Initial GET on mount.
  const fetchOnce = useCallback(() => {
    analyses
      .get(issueId)
      .then((data) => {
        if (isAnalysisFailed(data)) {
          // Terminal — never start the poll loop on a failed analysis.
          setState({ status: 'failed', message: data.error_message });
          return;
        }
        if (isAnalysisResult(data)) {
          setState({ status: 'completed', result: data });
        } else {
          setState({
            status: data.status,
            current_round: data.current_round,
            current_tool: data.current_tool,
          });
          startPolling();
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'not_started' });
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          setState({ status: 'failed', message: msg });
        }
      });
  }, [issueId, startPolling]);

  useEffect(() => {
    fetchOnce();
    return () => {
      stopPolling();
    };
  }, [fetchOnce, stopPolling]);

  const request = useCallback(
    async (
      model: AnalysisModel,
      promptVersion: PromptVersion = 'auto_triage_agentic.v1',
    ): Promise<void> => {
      try {
        await analyses.request(issueId, model, promptVersion);
        setState({ status: 'queued', current_round: null, current_tool: null });
        startPolling();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setState({ status: 'failed', message: msg });
      }
    },
    [issueId, startPolling],
  );

  const refetch = useCallback(() => {
    stopPolling();
    setState({ status: 'idle' });
    fetchOnce();
  }, [stopPolling, fetchOnce]);

  return { state, request, refetch };
}
